import { useCallback, useRef } from 'react'
import { useEvent, useIsomorphicLayoutEffect } from 'react-use'
import { useGSAP } from '@gsap/react'
import { cva, type VariantProps } from 'class-variance-authority'
import gsap from 'gsap'
import Image from 'next/image'

import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import { useLenis } from '@/contexts/lenis'
import { MediaQuery } from '@/constants'

import { handwriteImageRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

const imageVariants = cva(
  'relative h-auto w-full lg:h-full lg:w-auto overflow-hidden mx-auto',
  {
    variants: {
      variant: {
        'lifestyle-handw-01': 'aspect-[378/351] max-lg:max-w-[19.1rem]',
        'lifestyle-handw-02': 'aspect-[307/308] max-lg:max-w-[19.1rem]',
        'design-handw-01': 'aspect-[328/328] max-lg:max-w-[19.1rem]',
        'design-handw-02': 'aspect-[273/328] max-lg:max-w-[19.1rem]'
      }
    }
  }
)

export type HandwriteImageProps = VariantProps<typeof imageVariants> & {
  image: WPImage
}

const HandwriteImage = ({ variant, image }: HandwriteImageProps) => {
  const root = useRef<HTMLDivElement>(null!)
  const ratio = useRef<HTMLDivElement>(null!)
  const { lenis } = useLenis()

  const resize = useCallback(() => {
    const { width } = ratio.current.getBoundingClientRect()
    gsap.set(root.current, { width })
  }, [])

  useGSAP(
    () => {
      if (!lenis) return

      const matchMedia = gsap.matchMedia(root.current)

      matchMedia.add(MediaQuery.LARGE, () => {
        gsap
          .timeline({
            scrollTrigger: {
              trigger: root.current,
              start: 'left 100%',
              horizontal: true,
              markers: false,
              scrub: true
            }
          })
          .fromTo(root.current, { xPercent: 0 }, { xPercent: 50, ease: 'none' })
      })
    },
    { scope: root, dependencies: [lenis] }
  )

  useIsomorphicLayoutEffect(() => resize(), [resize])

  useEvent('resize', resize)

  return (
    <Section
      ref={root}
      className={cn('z-10', {
        'mb-spacing-lg lg:-mr-[5rem] lg:mb-0': variant === 'design-handw-02'
      })}
    >
      <Motion animation={handwriteImageRevealAnimation}>
        {(motionRef) => (
          <Grid ref={motionRef}>
            <div
              className={cn([
                'col-span-2 md:col-span-4 md:col-start-2',
                'lg:col-auto lg:row-span-4',
                {
                  'lg:row-start-4':
                    variant === 'lifestyle-handw-01' ||
                    variant === 'design-handw-01',
                  'lg:row-start-3':
                    variant === 'lifestyle-handw-02' ||
                    variant === 'design-handw-02'
                }
              ])}
            >
              <div
                ref={ratio}
                className={cn([
                  imageVariants({ variant }),
                  'will-change-transform'
                ])}
                data-motion-wrapper
              >
                <Image
                  src={image.url}
                  alt={image.alt}
                  loading="lazy"
                  fill
                  className="object-cover will-change-transform"
                  data-motion-content
                />
              </div>
            </div>
          </Grid>
        )}
      </Motion>
    </Section>
  )
}

export default HandwriteImage
